import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Users, Mail, Phone } from 'lucide-react';
import { useSchool } from '../../contexts/SchoolContext';
import toast from 'react-hot-toast';

interface TeamMember {
  id: string;
  displayName: string;
  email: string;
  role: 'admin' | 'staff';
  department?: string;
  title?: string;
  phone?: string;
  status: 'online' | 'away' | 'offline';
  lastActive?: string;
  joinedAt: string;
}

export default function TeamWidget() {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const { selectedSchool } = useSchool();

  useEffect(() => {
    if (!selectedSchool) {
      setTeam([]);
      setLoading(false);
      return;
    }

    // Subscribe to users in the selected school
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'school_members'),
        where('schoolId', '==', selectedSchool.id)
      ),
      async (snapshot) => {
        try {
          const memberPromises = snapshot.docs.map(async (memberDoc) => {
            const userDoc = await getDoc(doc(db, 'users', memberDoc.data().userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                id: userDoc.id,
                displayName: userData.fullName || userData.email.split('@')[0],
                email: userData.email,
                role: memberDoc.data().role,
                department: userData.department,
                title: userData.title,
                phone: userData.phone,
                status: userData.status || 'offline',
                lastActive: userData.lastActive,
                joinedAt: memberDoc.data().joinedAt
              } as TeamMember;
            }
            return null;
          });

          const members = (await Promise.all(memberPromises)).filter((member): member is TeamMember => member !== null);
          setTeam(members);
          setLoading(false);
        } catch (error) {
          console.error('Error loading team members:', error);
          toast.error('Failed to load team members');
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error loading team members:', error);
        toast.error('Failed to load team members');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedSchool]);

  if (loading) {
    return (
      <div className="bg-surface-light rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-text-primary">Team</h2>
          <Users className="w-5 h-5 text-text-secondary" />
        </div>

        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse flex items-center gap-4 p-4 rounded-lg bg-background"
            >
              <div className="w-10 h-10 bg-text-muted rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-text-muted rounded w-1/2 mb-2" />
                <div className="h-3 bg-text-muted rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!selectedSchool) {
    return (
      <div className="bg-surface-light rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-center h-32">
          <p className="text-text-secondary">Select a school to view team members</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-light rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Team</h2>
          <p className="text-sm text-text-secondary">{team.length} members</p>
        </div>
        <Users className="w-5 h-5 text-text-secondary" />
      </div>

      <div className="space-y-4">
        {team.map((member) => (
          <div
            key={member.id}
            className="p-4 rounded-lg bg-background hover:bg-primary-50 transition-colors"
          >
            <div className="flex items-start gap-4">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-lg font-medium text-primary-700">
                    {member.displayName?.split(' ').map(n => n[0]).join('') || 'U'}
                  </span>
                </div>
                <div
                  className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 
                    border-surface-light ${
                      member.status === 'online'
                        ? 'bg-success-DEFAULT'
                        : member.status === 'away'
                        ? 'bg-warning-DEFAULT'
                        : 'bg-text-muted'
                    }`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-medium text-text-primary">{member.displayName}</h3>
                    <p className="text-sm text-text-secondary">{member.title || member.role}</p>
                    {member.department && (
                      <p className="text-sm text-text-muted">{member.department}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    member.role === 'admin' 
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {member.role}
                  </span>
                </div>

                <div className="mt-3 space-y-1">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Mail className="w-4 h-4" />
                    <a 
                      href={`mailto:${member.email}`}
                      className="hover:text-primary-500 transition-colors"
                    >
                      {member.email}
                    </a>
                  </div>
                  {member.phone && (
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Phone className="w-4 h-4" />
                      <a 
                        href={`tel:${member.phone}`}
                        className="hover:text-primary-500 transition-colors"
                      >
                        {member.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {team.length === 0 && (
          <div className="text-center py-4">
            <p className="text-text-secondary">No team members found</p>
          </div>
        )}
      </div>
    </div>
  );
}